import axios from "axios";
import { API_BASE_PATH } from "../environment";
import { toast } from "react-toastify";

// ✅ Base URL
const BASE_URL = `${API_BASE_PATH}`;

// ✅ Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  };
};

// ✅ Error Handler
const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ LeadStage Interface
export interface LeadStage {
  LeadStage_id?: string;
  LeadStage_name: string;
  LeadStage_status?: string;
  created_at?: string;
}

// ✅ Create Lead Stage
export const createLeadStage = async (
  leadStageData: { LeadStage_name: string; LeadStage_status?: string }
): Promise<LeadStage | null> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/lead_stage_create/`,
      new URLSearchParams(leadStageData),
      { headers: getAuthHeaders() }
    );
    toast.success("Lead Stage created successfully!");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create lead stage.";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Update Lead Stage
export const updateLeadStage = async (
  leadstage_id: string,
  leadStageData: { LeadStage_name: string; LeadStage_status?: string }
): Promise<boolean> => {
  try {
    await axios.put(
      `${BASE_URL}/lead_stage/${leadstage_id}/update`,
      new URLSearchParams(leadStageData),
      { headers: getAuthHeaders() }
    );
    toast.success("Lead Stage updated successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Sources";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ Delete Lead Stage
export const deleteLeadStage = async (leadstage_id: string): Promise<boolean> => {
  try {
    await axios.delete(`${BASE_URL}/lead_stage/${leadstage_id}/delete`, {
      headers: getAuthHeaders(),
    });
    toast.success("Lead Stage deleted successfully!");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete lead stage.";
    toast.error(errorMessage);
    return false;
  }
};

// ✅ Get All Lead Stages
export const fetchAllLeadStages = async (): Promise<LeadStage[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/lead_stage_get/all`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch lead stages.";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get All Lead Stages
export const fetchAllLeadStagesActive = async (): Promise<LeadStage[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/lead_stage_get/all/active`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch lead stages.";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get Single Lead Stage by ID
export const fetchLeadStageById = async (
  leadstage_id: string
): Promise<LeadStage | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/lead_stage/${leadstage_id}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch lead stage details.";
    toast.error(errorMessage);
    return null;
  }
};


// ✅ Export all Lead Stages – Excel
export const exportLeadStageExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/lead_stage_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead stages exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead stages (Excel)");
    throw error;
  }
};

// ✅ Export all Lead Stages – PDF
export const exportLeadStagePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/lead_stage_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead stages exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead stages (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Lead Stages – Excel
export const exportSelectedLeadStageExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/lead_stage_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead stages exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead stages (Excel)");
    throw error;
  }
};

// ✅ Export selected Lead Stages – PDF
export const exportSelectedLeadStagePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/lead_stage_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead stages exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead stages (PDF)");
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Lead Stages
export const deleteLeadStageBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    await axios.delete(
      `${BASE_URL}/lead_stage_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    //toast.success("Lead stages deleted successfully");
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete lead stages.");
    } else {
      handleAxiosError(error, "Failed to delete lead stages");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface LeadStageImportResult {
  message: string;
  added: {
    lead_stage_name: string;
    lead_stage_status: string;
  }[];
  skipped: {
    row: number;
    lead_stage_name: string;
    reason: string;
  }[];
}

// ✅ Import Lead Stage Excel
export const importLeadStageExcel = async (
  file: File
): Promise<LeadStageImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${BASE_URL}/lead_stage_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Lead stage import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import lead stages");
    throw error;
  }
};